import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";
import { ApiError } from "./errors";

/**
 * Internet Distributor capabilities (owner 2026-10-26; contract radius-wiki
 * wiki/internet-distributor-capability-contract.md). Held by radaccess v1.185
 * in radius.radaccess_operator_capability, one row per TOP-LEVEL namespace.
 *
 *   GET  /access/operator/{ns}/capability                    the operator reads
 *   PUT  /access/operator/{ns}/capability                    superadmin: role, pin publishing
 *   POST /access/operator/{ns}/capability/accept             the Owner alone: declaration
 *   PUT  /access/operator/{ns}/capability/pin-sale-document  Owner/Admin: voucher | tax_invoice
 *
 * 🔑 The platform RECORDS what the Owner declares; it does not decide whether
 * they need a licence. A licensed VNO/ISP always invoices (AGR) whatever is
 * chosen here -- radbilling reads the licence row, not this choice.
 */

const enc = (s: string) => encodeURIComponent(s);
const base = (ns: string) => `/operator/${enc(ns)}/capability`;

export const DISTRIBUTOR_ROLE = "internet_distributor";

export type PinSaleDocument = "voucher" | "tax_invoice";
export type DeclaredLicense = "none" | "vno" | "isp";

export interface OperatorCapability {
  namespace: string;
  /** "internet_distributor" or "" (none). */
  businessRole: string;
  /** null = follows the provider type (ISP/VNO publish pins, others do not). */
  publishesPins: boolean | null;
  /** "" means the default, a voucher. */
  pinSaleDocument: string;
  responsibilityAcceptedAt?: string;
  responsibilityTextVersion?: string;
  declaredLicense?: string;
  /** Effective answer from radaccess: type and row together. */
  publishesWholesalePins: boolean;
  /** Role set by superadmin AND the Owner accepted the declaration. */
  distributor: boolean;
}

export const getCapability = (i: CallerIdentity, ns: string) =>
  callGateway<OperatorCapability>(i, config().gatewayBase, base(ns));

/** superadmin only. `true` makes the namespace an Internet Distributor; `false` clears the role. */
export const setDistributorRole = (i: CallerIdentity, ns: string, on: boolean) =>
  callGateway<OperatorCapability>(i, config().gatewayBase, base(ns), {
    method: "PUT",
    body: { businessRole: on ? DISTRIBUTOR_ROLE : "" },
  });

export const acceptDeclaration = (i: CallerIdentity, ns: string, declaredLicense: DeclaredLicense) =>
  callGateway<OperatorCapability>(i, config().gatewayBase, `${base(ns)}/accept`, {
    method: "POST",
    body: { textVersion: DECLARATION_VERSION, declaredLicense },
  });

export const setPinSaleDocument = (i: CallerIdentity, ns: string, document: PinSaleDocument) =>
  callGateway<OperatorCapability>(i, config().gatewayBase, `${base(ns)}/pin-sale-document`, {
    method: "PUT",
    body: { document },
  });

/**
 * ⚠️ DRAFT wording. The final text is the owner's and the CA's; when it
 * changes, bump the version so every acceptance names the text it accepted.
 * radaccess stores at most 32 characters.
 */
export const DECLARATION_VERSION = "draft-2026-10-27";

export const DECLARATION_POINTS: readonly string[] = [
  "I run this business in my own name. AnkhOS is the platform I use; it does not sell my pins, collect my money or add funds.",
  "I am responsible for any licence my internet business needs, and for the licence status I declare below being true.",
  "I am responsible for GST and other taxes on what I sell, and for any invoice or voucher I issue.",
  "If my licence status changes, I will update it here before my next sale.",
];

export const DECLARED_LICENSE_LABEL: Record<DeclaredLicense, string> = {
  none: "I do not hold a telecom licence",
  vno: "I hold a VNO licence",
  isp: "I hold an ISP licence",
};

/** The document a pin sale gives by default. */
export const effectivePinSaleDocument = (c: Pick<OperatorCapability, "pinSaleDocument">): PinSaleDocument =>
  c.pinSaleDocument === "tax_invoice" ? "tax_invoice" : "voucher";

/**
 * The documents a seller is offered on an approved pin order, the default
 * first. Only an ACTIVE distributor issues vouchers (radbilling refuses the
 * rest with 409); it still offers a tax invoice, because the owner's rule is
 * "a voucher for each sale, or a tax invoice in its own name when the buyer
 * asks" (2026-10-26). Everyone else invoices.
 */
export function pinSaleDocuments(c: Pick<OperatorCapability, "businessRole" | "distributor" | "pinSaleDocument"> | null | undefined): PinSaleDocument[] {
  if (!c || distributorStage(c) !== "active") return ["tax_invoice"];
  return effectivePinSaleDocument(c) === "voucher" ? ["voucher", "tax_invoice"] : ["tax_invoice", "voucher"];
}

/** What the card shows to whom. */
export type DistributorStage = "none" | "awaiting-declaration" | "active";

export function distributorStage(c: Pick<OperatorCapability, "businessRole" | "distributor"> | null | undefined): DistributorStage {
  if (!c || c.businessRole !== DISTRIBUTOR_ROLE) return "none";
  return c.distributor ? "active" : "awaiting-declaration";
}

const REFUSALS: Record<string, string> = {
  capabilities_not_available_yet: "Distributor settings are not switched on yet on this platform.",
  no_operator_profile: "This namespace has no provider profile yet. Save the profile first.",
  distributor_must_be_top_level: "Only a top-level provider can be an Internet Distributor. This one is a sub-operator.",
  distributor_type_not_allowed: "Only an Operator, VNO or ISP can be an Internet Distributor.",
  not_a_distributor: "This provider is not an Internet Distributor.",
  invalid_value: "That value was refused.",
};

export function capabilityRefusal(e: unknown): string {
  if (e instanceof ApiError) {
    const words = REFUSALS[e.message];
    if (words) return words;
    if (e.kind === "forbidden") return "Refused: your role may not do this for this namespace.";
  }
  return e instanceof Error ? e.message : String(e);
}
